import { useCallback, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { Theme } from '@app/theme';
import { Button, useToast } from '@app/core';
import { ErrorMsg } from '@app/form';
import { Invoice, useConfirmInvoicePayment } from '@app/api';
import { toEuros } from 'lib/numbers';
import { PaymentMethodContainer } from 'components/BillingInformation/PaymentMethodContainer';
import { useUsablePaymentMethods } from 'components/BillingInformation/useUsablePaymentMethods';
import { PaymentMethodBlock } from 'components/BillingInformation/PaymentMethodBlock';
import * as Stripe from '@stripe/stripe-js';

const useStyles = createUseStyles((theme: Theme) => ({
  selectedIcon: {
    color: theme.colors.green,
    fontSize: theme.iconSize.regular,
  },
  newCardContent: {
    flex: 1,
  },
  newCardLabel: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
  },
  newCardInfo: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginBottom: theme.marginBase,
  },
  cardContainer: {
    marginTop: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
    padding: theme.marginBase * 2,
    minHeight: theme.height.regular,
    display: 'flex',
    alignItems: 'center',
  },
  cardElement: {
    flex: 1,
  },
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: theme.marginBase * 4,
  },
  error: {
    marginTop: theme.marginBase * 2,
  },
}));

const NEW_CARD = 'NEW_CARD';

interface Props {
  invoice: Invoice;
  clientSecret: string;
  onSuccess?: (invoice: Invoice) => void;
  intent: Stripe.PaymentIntent;
}

export const PaymentIntentForm = ({
  invoice,
  clientSecret,
  onSuccess,
  intent,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const stripe = useStripe();
  const elements = useElements();

  const { paymentMethods, isLoading } = useUsablePaymentMethods(
    invoice?.organizationId,
    invoice?.sessionId,
  );
  const { mutateAsync: markAsPaid } = useConfirmInvoicePayment(invoice.id);

  const [selected, setSelected] = useState<string | undefined>();
  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const selectNewCard = useCallback(() => setSelected(NEW_CARD), []);

  const pay = useCallback(async () => {
    if (!stripe || !elements || !selected) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(undefined);

    const { error } = await stripe.confirmCardPayment(clientSecret, {
      payment_method:
        selected === NEW_CARD
          ? { card: elements.getElement(CardElement)! }
          : selected,
    });

    if (error) {
      if (error.type === 'validation_error') {
        toast.invalidForm();
      } else {
        toast.exception();
      }
      setErrorMessage(error.message);
      return;
    }

    const paidInvoice = await markAsPaid({ intentId: intent.id });

    setSubmitting(false);
    onSuccess?.(paidInvoice);
  }, [
    stripe,
    elements,
    selected,
    clientSecret,
    toast,
    onSuccess,
    markAsPaid,
    intent,
  ]);

  return (
    <>
      <div>
        {paymentMethods?.map((paymentMethod) => (
          <PaymentMethodBlock
            key={paymentMethod.id}
            paymentMethod={paymentMethod}
            onClick={setSelected}
            selected={paymentMethod.id === selected}
          />
        ))}

        {isLoading && !paymentMethods && (
          <>
            <PaymentMethodBlock onClick={setSelected} />
            <PaymentMethodBlock onClick={setSelected} />
            <PaymentMethodBlock onClick={setSelected} />
          </>
        )}
      </div>

      {!!paymentMethods.length && (
        <PaymentMethodContainer
          selectable
          selected={selected === NEW_CARD}
          onClick={selectNewCard}
        >
          {selected === NEW_CARD && (
            <i
              className={classNames(
                'ri-checkbox-circle-fill',
                classes.selectedIcon,
              )}
            />
          )}
          <div className={classes.newCardContent}>
            <div className={classes.newCardLabel}>
              {t('billing.payInvoice.newCard')}
            </div>
          </div>
        </PaymentMethodContainer>
      )}

      {(selected === NEW_CARD || !paymentMethods.length) && (
        <div className={classes.cardContainer}>
          <CardElement className={classes.cardElement} />
        </div>
      )}

      <ErrorMsg error={errorMessage} className={classes.error} />

      <div className={classes.buttonsLine}>
        <Button
          text={t('billing.payInvoice.pay', {
            amount: toEuros(intent.amount),
          })}
          onClick={pay}
          disabled={!selected || !stripe || !elements}
          isLoading={isSubmitting}
        />
      </div>
    </>
  );
};
